import { computed } from 'vue';
import { toValue } from '../../modules/vueuse/toValue';
import { useDataStore } from '../useVuexStore';
import { useTraits } from '../useTraits';
import { getComplexUpcKey } from '../../utils/getComplexUpcKey';
import { UPC } from '../../store_modules/traitsStore/types';

function toTraitsMap(upcMap, upcs) {
  return Object.keys(upcMap).map((complexKey) => {
    const split = complexKey.split('_');
    const color = split[0];
    const size = split[1];
    const type = split[2];
    const upcId = upcMap[complexKey];
    const availability = upcs[upcId]?.availability;
    const available = !!(availability?.available || availability?.bopsAvailable);

    return {
      color,
      size,
      type,
      complexKey,
      upcId,
      available,
    };
  });
}

function connectToStore() {
  return useDataStore(UPC);
}

/**
 *
 * @param {object} obj
 * @param {ProductId} obj.id
 * @param {import('@composables/useUpc/types').UseUpc.UpcData | any} obj.data
 */
export function useInitUpc({ id, data = {} }) {
  connectToStore().set(id, data);
}

function isDefined(trait) {
  return typeof trait === 'number' || typeof trait === 'string';
}

/**
 *
 * @param {import('@vueuse/core').MaybeRef<ProductId>} productId
 */
export function useUpc(productId) {
  const storeData = connectToStore();
  const { selectedSizeId, selectedColorId, selectedTypeId } = useTraits(productId);
  const upcData = computed(() => storeData.get(toValue(productId)) || { upcMap: {}, upcs: {} });

  // TODO Some of product doesn't have size. So we need to check if traits lists of size, color, type, orientation, etc. exist.
  const selectedUpcId = computed(() => {
    const colorId = selectedColorId.value;
    const sizeId = selectedSizeId.value; // ! For some reason id could be a zero
    const typeId = selectedTypeId.value;

    const complexKey = getComplexUpcKey(colorId, sizeId, typeId);
    // Handles an edgecase for bcom when orientation exists and only a single size "No Size" is defined, but is missing from upcMap
    const complexKey2 = isDefined(typeId) ? getComplexUpcKey(colorId, typeId) : '';

    return upcData.value.upcMap[complexKey] || upcData.value.upcMap[complexKey2];
  });

  const selectedUpcNumber = computed({
    get() {
      return upcData
        .value
        .upcs[selectedUpcId.value]
        ?.number || '';
    },
    set(value) {
      const upc = upcData.value.upcs[value];
      if (!upc) {
        return;
      }
      if (upc.traits.sizes?.selectedSize !== undefined) {
        selectedSizeId.value = upc.traits.sizes.selectedSize;
      }
      if (upc.traits.colors?.selectedColor !== undefined) {
        selectedColorId.value = upc.traits.colors.selectedColor;
      }
      if (upc.traits.types?.selectedType !== undefined) {
        selectedTypeId.value = upc.traits.types.selectedType;
      }
    },
  });

  const hasSelectedUpcId = computed(() => !!selectedUpcId.value);
  const hasSelectedUpcNumber = computed(() => !!selectedUpcNumber.value);
  const traitsMap = computed(() => toTraitsMap(upcData.value.upcMap, upcData.value.upcs));

  return {
    hasSelectedUpcId,
    hasSelectedUpcNumber,
    selectedUpcId,
    selectedUpcNumber,
    upcData,
    traitsMap,
  };
}

export default useUpc;
